<template>
  <div class="header-container">
    <b-navbar toggleable="lg" class="navbar-custom">
      <!-- Mobile-only brand: on desktop the logo already lives in the Sidebar -->
      <b-navbar-brand to="/" class="d-lg-none mobile-brand">
        <img :src="mobileLogoSrc" alt="Zerticarbon" class="mobile-logo">
      </b-navbar-brand>

      <!-- Always-visible mobile controls: theme toggle + the single hamburger -->
      <div class="mobile-controls d-flex d-lg-none align-items-center ml-auto">
        <button class="theme-toggle-btn mr-2" @click="toggleTheme" aria-label="Toggle theme">
          <span class="icon-swap" :class="{ 'is-dark': theme === 'dark' }">
            <i class="fas fa-sun sun"></i>
            <i class="fas fa-moon moon"></i>
          </span>
        </button>
        <button v-if="pageTourKey" class="tour-replay-btn mr-2" title="Replay guided tour"
                aria-label="Replay guided tour" @click="replayTour">
          <i class="fas fa-question"></i>
        </button>
        <router-link v-if="isWeb2UserConnected" to="/notifications" class="bell-btn mr-2" title="Notifications"
                     aria-label="Notifications">
          <i class="fas fa-bell"></i>
          <span v-if="unreadCount" class="bell-badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
        </router-link>
        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
      </div>

      <b-collapse id="nav-collapse" is-nav>
        <!-- Mobile-only unified navigation: desktop already has the Sidebar -->
        <b-navbar-nav class="mobile-nav-links d-lg-none" @click="closeMobileMenu">
          <li class="nav-item">
            <router-link to="/areas-map" class="nav-link"><i class="fas fa-map-marked-alt nav-icon"></i> Forests Map</router-link>
          </li>

          <template v-if="isAdmin">
            <li class="nav-title">Administration</li>
            <li class="nav-item">
              <router-link to="/areas-list" class="nav-link"><i class="fas fa-list-ul nav-icon"></i> Forest List</router-link>
            </li>
            <li class="nav-item">
              <router-link to="/statistics" class="nav-link"><i class="fas fa-chart-line nav-icon"></i> Statistics</router-link>
            </li>
            <li class="nav-item">
              <router-link to="/simulator" class="nav-link"><i class="fas fa-calculator nav-icon"></i> Simulator</router-link>
            </li>
            <li class="nav-item">
              <router-link to="/tree-types" class="nav-link"><i class="fas fa-seedling nav-icon"></i> Tree Types</router-link>
            </li>
            <li class="nav-item">
              <router-link to="/customers" class="nav-link"><i class="fas fa-users nav-icon"></i> Customers</router-link>
            </li>
            <li class="nav-item">
              <router-link to="/companies" class="nav-link"><i class="fas fa-building nav-icon"></i> Companies</router-link>
            </li>
          </template>

          <template v-if="isAuthUser && !isAdmin">
            <li class="nav-item">
              <router-link to="/areas-list" class="nav-link"><i class="fas fa-tree nav-icon"></i> My Forests</router-link>
            </li>
          </template>

          <li class="nav-item" v-if="isWeb2UserConnected">
            <router-link to="/retire-tokens" class="nav-link"><i class="fas fa-leaf nav-icon"></i> Retire CO2</router-link>
          </li>

          <!-- Marketplace and My Orders hidden from the menu for now (the pages still exist) -->
          <li class="nav-item" v-if="isWeb2UserConnected" style="display: none">
            <router-link to="/marketplace" class="nav-link"><i class="fas fa-store nav-icon"></i> Marketplace</router-link>
          </li>

          <li class="nav-item" v-if="isWeb2UserConnected" style="display: none">
            <router-link to="/my-orders" class="nav-link"><i class="fas fa-receipt nav-icon"></i> My Orders</router-link>
          </li>

          <li class="nav-item" v-if="isWeb2UserConnected">
            <router-link to="/notifications" class="nav-link">
              <i class="fas fa-bell nav-icon"></i> Notifications
              <span v-if="unreadCount" class="nav-badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
            </router-link>
          </li>

          <template v-if="config && (config.exchange_url || config.liquidity_url)">
            <li class="nav-title">External</li>
            <li class="nav-item" v-if="config.exchange_url">
              <a :href="config.exchange_url" target="_blank" class="nav-link"><i class="fas fa-exchange-alt nav-icon"></i> Exchange</a>
            </li>
            <li class="nav-item" v-if="config.liquidity_url">
              <a :href="config.liquidity_url" target="_blank" class="nav-link"><i class="fas fa-water nav-icon"></i> Liquidity</a>
            </li>
          </template>

          <li class="nav-title-divider"></li>
        </b-navbar-nav>

        <b-navbar-nav>
          <!-- Left aligned items (empty for now) -->
        </b-navbar-nav>

        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto align-items-center">

          <!-- Theme Switcher (desktop only — mobile has its own always-visible one above) -->
          <button class="theme-toggle-btn mr-2 d-none d-lg-flex" @click="toggleTheme" aria-label="Toggle theme">
            <span class="icon-swap" :class="{ 'is-dark': theme === 'dark' }">
              <i class="fas fa-sun sun"></i>
              <i class="fas fa-moon moon"></i>
            </span>
          </button>

          <!-- Replay the guided tour for this page, when it has one -->
          <button v-if="pageTourKey" class="tour-replay-btn mr-2" title="Replay guided tour"
                  aria-label="Replay guided tour" @click="replayTour">
            <i class="fas fa-question"></i>
          </button>

          <!-- Notifications bell (mobile has its own always-visible one above) -->
          <router-link v-if="isWeb2UserConnected" to="/notifications" class="bell-btn mr-2 d-none d-lg-flex"
                       title="Notifications" aria-label="Notifications">
            <i class="fas fa-bell"></i>
            <span v-if="unreadCount" class="bell-badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
          </router-link>

          <div v-if="getWeb2ActiveAccount?.role.name === 'Administrator' && buffer.buffer != undefined"
               class="stat-chip mr-2">
            <span class="stat-icon"><i class="fas fa-shield-alt"></i></span>
            <span class="stat-meta">
              <span class="stat-label">Buffer</span>
              <span class="stat-value">{{
                  new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(roundNum(buffer.buffer)) + ' ZCO2'
                }} <em>({{ buffer.bufferPercent }}%)</em></span>
            </span>
          </div>

          <div v-if="getWeb2ActiveAccount?.role.name === 'Administrator' && adminStats.balance != undefined"
               class="stat-chip mr-2">
            <span class="stat-icon"><i class="fas fa-seedling"></i></span>
            <span class="stat-meta">
              <span class="stat-label">Available</span>
              <span class="stat-value">{{
                  new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(roundNum(adminStats.balance)) + ' ZCO2'
                }}</span>
            </span>
          </div>

          <div v-if="isUserConnected && getTokenBalance != undefined"
               class="stat-chip stat-chip-interactive mr-2"
               v-on:mouseleave="showBalanceQty" v-on:mouseover="showBalancePrice">
            <span class="stat-icon"><img alt="" src="@/assets/img/MetaMask-white.png" style="height: 16px" @click.stop="addToMetamask('ZCO2')"></span>
            <span class="stat-meta">
              <span class="stat-label">Balance</span>
              <span class="stat-value" v-if="balanceShow == 'price' && stats.co2Price">{{
                  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roundNum(this.getTokenBalance * stats.co2Price)) + ' €'
                }}</span>
              <span class="stat-value" v-else>{{
                  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roundNum(this.getTokenBalance)) + ' ZCO2'
                }}</span>
            </span>
          </div>

          <router-link v-if="isUserConnected && getLockedTokenBalance != undefined" to="/retire-tokens"
               class="stat-chip stat-chip-interactive stat-chip-accent mr-2"
               v-on:mouseleave.native="showLockedQty" v-on:mouseover.native="showLockedPrice">
            <span class="stat-icon"><img alt="" src="@/assets/img/MetaMask-white.png" style="height: 16px" @click.stop.prevent="addToMetamask('RZCO2')"></span>
            <span class="stat-meta">
              <span class="stat-label">Retired</span>
              <span class="stat-value" v-if="lockedShow == 'price' && stats.co2Price">{{
                  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roundNum(this.getLockedTokenBalance * stats.co2Price)) + ' €'
                }}</span>
              <span class="stat-value" v-else>{{
                  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roundNum(this.getLockedTokenBalance)) + ' RZCO2'
                }}</span>
            </span>
          </router-link>

          <b-nav-item v-if="!isUserConnected && !isWeb2UserConnected" right>
            <router-link to="/dashboard">
              <b-button variant="primary" class="header-btn">
                Connect
              </b-button>
            </router-link>
          </b-nav-item>
          <b-dropdown v-else class="my-2 user-dropdown" right no-caret>
            <template #button-content>
              <span class="avatar-chip">{{ accountInitial }}</span>
              <span class="d-none d-md-inline">Account</span>
              <i class="fas fa-chevron-down ml-1 dropdown-caret"></i>
            </template>
            <b-dropdown-item v-if="isWeb2UserConnected" href="#" to="/profile">
              Profile
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item v-if="!isUserConnected && !isWeb2UserConnected" href="#" to="/dashboard">Sign in
            </b-dropdown-item>
            <b-dropdown-header>Web2</b-dropdown-header>
            <b-dropdown-text v-if="isWeb2UserConnected"><strong>Email:</strong>
              {{ getWeb2ActiveAccount.email }}...
            </b-dropdown-text>
            <b-dropdown-item v-if="isWeb2UserConnected" href="#" @click="disconnectAccount">Disconnect</b-dropdown-item>
            <b-dropdown-item v-else href="#" to="/dashboard">Not logged in.</b-dropdown-item>

            <b-dropdown-divider/>
            <b-dropdown-header>Web3</b-dropdown-header>
            <b-dropdown-text v-if="isUserConnected"><strong>Chain:</strong> {{ getChainName }}
            </b-dropdown-text>
            <b-dropdown-text v-if="isUserConnected"><strong>Address:</strong>
              {{ getActiveAccount.substring(0, 7) }}...
            </b-dropdown-text>
            <b-dropdown-item v-if="isUserConnected" href="#" @click="disconnectWeb3Modal">Disconnect</b-dropdown-item>
            <!-- /dashboard is the sign-in page now: a signed-in user was bounced home without connecting anything -->
            <b-dropdown-item v-else-if="isWeb2UserConnected" href="#" @click="connectWallet">
              <i class="fas fa-wallet mr-1"></i> Connect wallet
            </b-dropdown-item>
            <b-dropdown-item v-else href="#" to="/dashboard">Not logged in.</b-dropdown-item>
          </b-dropdown>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import Stats from "../../api/Stats";
import Config from "../../api/Config";
import {roundDown} from "../../utils/roundDown";
import {currentTourKey} from "../../utils/tour";
import Notification from "../../api/Notification";
import socket from "../../utils/socket";
import {connectWallet} from "../../utils/wallet";
import logoWhite from "@/assets/logo.png";
import logoGreen from "@/assets/img/zerticarbon_green_logo.png";

export default {
  name: 'Header',
  data() {
    return {
      balanceShow: null,
      lockedShow: null,
      config: null,
      stats: {},
      adminStats: {},
      buffer: {},
      appVersion: process.env.VUE_APP_VERSION || "",
      theme: 'dark', // Default theme
      unreadCount: 0,
      unsubscribeNotifications: null,
      refreshTimer: null
    }
  },
  created() {
    Config.getConfig()
      .then((config) => {
        this.config = config.data
      }).catch((error) => {
      console.log(error)
    })
    this.refreshAdminStats();
    Stats.getBuffer()
      .then((buffer) => {
        // null when the on-chain read fails (RPC down, or the dev chain): the chip just stays hidden
        this.buffer = buffer.data || {}
      }).catch((error) => {
      console.log(error)
    })
    this.showLockedQty();
    this.showBalanceQty();

    this.refreshTimer = setInterval(() => {
      this.updateBalance();
      this.updateLockedBalance();
      // "Available" is recalculated by the server every 30 minutes; re-read it so an open page does not show a stale figure.
      this.refreshAdminStats();
    }, 60000);

    // Initialize theme
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      this.theme = savedTheme;
    }
    document.documentElement.setAttribute('data-theme', this.theme);

    this.initNotifications();
    this.$root.$on('zc-notifications:read', this.refreshUnreadCount);
  },
  beforeDestroy() {
    // Without this every re-creation of the header (hot reload, layout change) left another timer polling forever.
    clearInterval(this.refreshTimer);
    if (this.unsubscribeNotifications) this.unsubscribeNotifications();
    this.$root.$off('zc-notifications:read', this.refreshUnreadCount);
  },
  watch: {
    isWeb2UserConnected(connected) {
      if (connected) {
        this.initNotifications();
      } else {
        this.unreadCount = 0;
        if (this.unsubscribeNotifications) this.unsubscribeNotifications();
        socket.disconnect();
      }
    }
  },
  computed: {
    ...mapGetters("tokenContract", ["getTokenBalance"]),
    ...mapGetters("lockedTokenContract", ["getLockedTokenBalance"]),
    ...mapGetters("user", ["isWeb2UserConnected", "getWeb2ActiveAccount"]),
    ...mapGetters("accounts", ["getChainName", "getActiveAccount", "isUserConnected", "getWeb3Modal"]),
    accountInitial() {
      if (this.isWeb2UserConnected && this.getWeb2ActiveAccount) {
        const account = this.getWeb2ActiveAccount;
        const name = account.firstName || account.username || account.email;
        return name.charAt(0).toUpperCase();
      }
      if (this.isUserConnected && this.getActiveAccount) {
        return this.getActiveAccount.charAt(2).toUpperCase();
      }
      return '?';
    },
    isAdmin() {
      return this.isWeb2UserConnected && this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role.name === 'Administrator';
    },
    isAuthUser() {
      return this.isWeb2UserConnected && this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role.name === 'Authenticated';
    },
    mobileLogoSrc() {
      // The mobile header background is white in light mode, so the
      // white-on-transparent logo needs to swap for the green version.
      return this.theme === 'light' ? logoGreen : logoWhite;
    },
    pageTourKey() {
      return currentTourKey(this.$route);
    }
  },

  methods: {
    ...mapActions("user", ["disconnectAccount"]),
    ...mapActions("accounts", ["disconnectWeb3Modal"]),
    ...mapActions("tokenContract", ["updateBalance"]),
    ...mapActions("lockedTokenContract", ["updateLockedBalance"]),
    connectWallet() {
      return connectWallet(this.$store, this.$router);
    },
    roundNum(number, decimals) {
      return roundDown(number, decimals)
    },
    showBalancePrice() {
      this.balanceShow = 'price';
    },
    showBalanceQty() {
      this.balanceShow = 'balance';
    },
    showLockedPrice() {
      this.lockedShow = 'price';
    },
    showLockedQty() {
      this.lockedShow = 'balance';
    },
    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', this.theme);
      document.documentElement.setAttribute('data-theme', this.theme);
    },
    closeMobileMenu() {
      this.$root.$emit('bv::toggle::collapse', 'nav-collapse');
    },
    replayTour() {
      // The page component itself owns its tour steps (it knows which of
      // its own elements exist) — this just asks the current page to run
      // it again, ignoring the "already seen" flag.
      this.$root.$emit('zc-tour:replay', this.pageTourKey);
    },
    refreshAdminStats() {
      Stats.getAdminStats()
        .then((adminStats) => {
          this.adminStats = adminStats.data
        }).catch((error) => {
          console.log(error)
        })
    },
    refreshUnreadCount() {
      if (!this.isWeb2UserConnected) return;
      Notification.getUnreadCount()
        .then((res) => {
          this.unreadCount = res.data.count;
        }).catch(() => {});
    },
    initNotifications() {
      if (!this.isWeb2UserConnected) return;

      this.refreshUnreadCount();

      if (this.unsubscribeNotifications) this.unsubscribeNotifications();
      this.unsubscribeNotifications = socket.onNotification((payload) => {
        this.unreadCount += 1;
        // Only fires if the browser channel is on for this type AND the
        // user already granted permission — requesting permission itself
        // happens explicitly from the Notifications preferences page, never
        // unprompted here.
        if (window.Notification && window.Notification.permission === 'granted') {
          const n = new window.Notification(payload.title, {
            body: payload.message,
            icon: logoGreen
          });
          if (payload.link) {
            n.onclick = () => {
              window.focus();
              this.$router.push(payload.link);
            };
          }
        }
      });
    },
    async addToMetamask(token) {
      try {
        let options = {}
        switch (token) {
          case 'RZCO2':
            options = {
              address: this.config.locked_token_contract_address,
              symbol: token,
              decimals: 18,
            };
            break
          default:
            options = {
              address: this.config.token_contract_address,
              symbol: token,
              decimals: 18,
            };
        }

        const wasAdded = await window.ethereum.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options,
          },
        });

        if (wasAdded) {
          console.log('Thanks for your interest!');
        } else {
          console.log('Your loss!');
        }
      } catch (error) {
        console.log(error);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.header-container {
  background: var(--header-bg);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border-bottom: 1px solid var(--header-border);
  transition: background-color 0.3s ease;
  position: sticky;
  top: 0;
  z-index: 900;
}

.navbar-custom {
  padding: 0.65rem 1.5rem;
}

.mobile-brand {
  display: flex;
  align-items: center;
  margin-right: auto;
}

.mobile-logo {
  height: 30px;
  object-fit: contain;
}

.mobile-controls {
  flex-shrink: 0;
}

::v-deep .mobile-nav-links {
  width: 100%;
  padding: 6px 0 10px;
  border-top: 1px solid var(--border-subtle);
  margin-top: 8px;

  .nav-title {
    padding: 12px 6px 6px;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-muted);
    font-weight: 700;
  }

  .nav-title-divider {
    height: 1px;
    background-color: var(--border-subtle);
    margin: 10px 0;
  }

  .nav-item {
    list-style: none;
  }

  .nav-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 6px;
    // Bootstrap's default navbar-light `.nav-link` color (rgba(0,0,0,.5))
    // otherwise wins over the theme color here, making text unreadable in
    // dark mode.
    color: var(--text-main) !important;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 500;
    transition: background-color 0.18s ease, color 0.18s ease;

    .nav-icon {
      width: 18px;
      text-align: center;
      color: var(--accent-green);
    }

    &:hover, &.router-link-exact-active {
      background-color: rgba(var(--accent-green-rgb), 0.1);
      color: var(--accent-green) !important;
    }
  }
}

::v-deep .navbar-toggler {
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  color: var(--text-main);
  padding: 0.35rem 0.55rem;

  .navbar-toggler-icon {
    background-image: none;
    position: relative;
    width: 18px;
    height: 14px;
    display: block;

    &::before, &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--text-main);
      border-radius: 2px;
    }
    &::before { top: 0; box-shadow: 0 6px 0 var(--text-main); }
    &::after { bottom: 0; }
  }

  &:hover {
    border-color: var(--accent-green);
  }
}

// Theme toggle
.theme-toggle-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid var(--border-subtle);
  background: var(--bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.15s ease;
  flex-shrink: 0;

  &:hover {
    border-color: var(--accent-green);
    box-shadow: 0 0 0 4px rgba(var(--accent-green-rgb), 0.14);
  }

  &:active {
    transform: scale(0.92);
  }
}

.icon-swap {
  position: relative;
  width: 18px;
  height: 18px;
  display: block;

  i {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
  }

  .sun {
    color: #e8a83e;
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }

  .moon {
    color: #8fb3ff;
    transform: rotate(90deg) scale(0);
    opacity: 0;
  }

  &.is-dark {
    .sun {
      transform: rotate(-90deg) scale(0);
      opacity: 0;
    }
    .moon {
      transform: rotate(0deg) scale(1);
      opacity: 1;
    }
  }
}

// Stat chips (balances)
.stat-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 8px;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s ease, border-color 0.2s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
    border-color: rgba(var(--accent-green-rgb), 0.4);
  }
}

.stat-chip-accent {
  border-color: rgba(var(--co2-accent-rgb), 0.35);

  .stat-icon {
    background: rgba(var(--co2-accent-rgb), 0.14);
  }
}

.stat-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(var(--accent-green-rgb), 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-green);
  font-size: 0.75rem;
  flex-shrink: 0;

  img {
    border-radius: 50%;
  }
}

.stat-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.stat-label {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  font-weight: 700;
}

.stat-value {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-main);

  em {
    font-style: normal;
    color: var(--text-muted);
    font-weight: 500;
  }
}

.header-btn {
  background: var(--accent-gradient) !important;
  border-color: transparent !important;
  color: #fff !important;
  font-weight: 600;

  &:hover {
    filter: brightness(1.1);
  }
}

.avatar-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--accent-gradient);
  color: #fff;
  font-weight: 700;
  font-size: 0.75rem;
  margin-right: 6px;
}

.dropdown-caret {
  font-size: 0.65rem;
  color: var(--text-muted);
}

::v-deep .user-dropdown {
  .dropdown-toggle {
    display: flex;
    align-items: center;
    background: var(--bg-elevated) !important;
    border: 1px solid var(--border-subtle) !important;
    color: var(--text-main) !important;
    border-radius: 999px;
    padding: 4px 14px 4px 4px;
    font-weight: 600;
    font-size: 0.85rem;
    box-shadow: var(--shadow-sm);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

    &:hover, &:focus {
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
      border-color: rgba(var(--accent-green-rgb), 0.4) !important;
    }

    &::after {
      display: none;
    }
  }

  &.show .dropdown-toggle {
    box-shadow: 0 0 0 3px rgba(var(--accent-green-rgb), 0.18);
  }

  // The site-wide .dropdown-menu glass effect (app.scss) is translucent
  // enough to blend into busy page backgrounds (e.g. a data table) — this
  // menu carries the account email/wallet, so it needs to stay legible over
  // anything behind it rather than following that glass look.
  .dropdown-menu {
    background: var(--card-bg);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}
</style>
